import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import { Menu, Search, BookOpen } from 'lucide-react';
import { CommandPalette } from './CommandPalette';
import { NotificationBell } from './NotificationBell';
import { UserManualPanel } from './UserManualPanel';
import { useLanguage } from '../contexts/LanguageContext';
import { TenantReadOnlyProvider } from '../contexts/TenantReadOnlyContext';
import TenantReadOnlyBanner from './layout/TenantReadOnlyBanner';
import FioriShell from './fiori/FioriShell';
import '../styles/sidebar.css';

// Feature-Flag: SAP-Fiori-Horizon-Shell (Top-Nav + Launchpad) ist Standard.
// Auf "false" setzen, um die Legacy-Sidebar-Shell zu reaktivieren (Rollback).
const FIORI_ENABLED = process.env.REACT_APP_FIORI_LAUNCHPAD !== 'false';

const PAGE_TITLES = {
  '/home': 'Dashboard',
  '/complaints': 'Reklamationen',
  '/analytics': 'Auswertung & Berichte',
  '/user-management': 'Benutzerverwaltung',
  '/ai-configuration': 'KI-Konfiguration',
  '/email': 'E-Mail Eingang',
  '/catalogs': 'Katalogverwaltung',
  '/role-matrix': 'Rollenmatrix',
  '/audit-log': 'Audit-Log',
  '/theme-settings': 'Design-Einstellungen',
  '/datenschutz': 'Datenschutz',
  '/impressum': 'Impressum',
  '/hilfe': 'Hilfe & Support',
};

// Legacy-Shell (Sidebar) — Fallback, aktiv nur bei REACT_APP_FIORI_LAUNCHPAD=false
function LegacyAppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { t } = useLanguage();
  const location = useLocation();

  // Dynamischer Tab-Titel
  useEffect(() => {
    const base = 'QM8D.ai';
    const path = location.pathname;
    const page = PAGE_TITLES[path] || Object.entries(PAGE_TITLES).find(([k]) => path.startsWith(k))?.[1] || '';
    document.title = page ? `${page} — ${base}` : `${base} — Quality Intelligence`;
  }, [location.pathname]);

  // Sidebar-Collapsed-State mit localStorage-Persistenz (kein Auto-Collapse)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar_is_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const handleToggleCollapse = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar_is_collapsed', String(next));
      return next;
    });
  };

  // Command Palette State
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  // Cmd+K / Ctrl+K Listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <TenantReadOnlyProvider>
    <div className="app-layout">
      <TenantReadOnlyBanner />
      {/* Mobile hamburger */}
      <button
        className={`sidebar-mobile-toggle ${sidebarOpen ? 'sidebar-open-hidden' : ''}`}
        onClick={() => setSidebarOpen(true)}
        data-testid="mobile-menu-toggle"
        aria-label={t('appLayout.openMenu') || 'Menü öffnen'}
      >
        <Menu size={20} />
      </button>

      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleCollapse}
      />

      <div className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Global Header Bar */}
        <header className="sticky top-0 z-30 flex items-center justify-end gap-2 px-4 py-2 glass-surface bg-white/80 border-b border-slate-200/60" data-testid="global-header" role="banner">
          {/* Suche */}
          <button
            onClick={() => setPaletteOpen(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-slate-500 bg-slate-100/80 border border-transparent hover:bg-slate-200/60 hover:border-blue-500/20 transition-all duration-200"
            data-testid="search-trigger"
            aria-label={t('common.searchPlaceholder') || 'Suche öffnen'}
          >
            <Search className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t('common.searchPlaceholder')}</span>
            <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[9px] font-mono bg-white border border-slate-200 rounded ml-2 text-slate-400">Ctrl+K</kbd>
          </button>

          {/* Benachrichtigungen */}
          <NotificationBell />

          {/* Benutzerhandbuch */}
          <button
            onClick={() => setManualOpen(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-slate-500 bg-slate-100/80 border border-transparent hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200/50 transition-all duration-200"
            data-testid="user-manual-btn"
            aria-label="Benutzerhandbuch"
          >
            <BookOpen className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Hilfe</span>
          </button>
        </header>

        <main id="main-content" role="main" className="min-w-0 overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      {/* Command Palette */}
      <CommandPalette isOpen={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {/* Benutzerhandbuch Panel */}
      <UserManualPanel isOpen={manualOpen} onClose={() => setManualOpen(false)} />
    </div>
    </TenantReadOnlyProvider>
  );
}

// Shell-Auswahl: Fiori-Horizon (Standard) oder Legacy-Sidebar (Rollback-Flag)
function AppLayout() {
  return FIORI_ENABLED ? <FioriShell /> : <LegacyAppLayout />;
}

export default AppLayout;
